import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../Interceptor";
import { DeleteOutlined } from "@ant-design/icons";
import { getRoute } from "../api/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import CustomModal from "./customModal/customModal";
import { Button, DatePicker, Descriptions } from "antd";
import { CustomInput, CustomSelect, ErrorText } from "./ui";
import {
  validateFormHandler,
  validateLiveField,
} from "../utils/validations/validateForm";
import ModalFooter from "./customModal/modalFooter";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AddProject = ({ isOpen, toggleModal, fetchProjects }) => {
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasMilestone, setHasMilestone] = useState(false);
  const [milestones, setMilestones] = useState([
    { name: "", startDate: dayjs().format("YYYY-MM-DD"), endDate: "" },
  ]);
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const contentRef = useRef(null);

  const handleAddMilestone = (e) => {
    e.preventDefault();
    setMilestones([...milestones, { name: "", startDate: "", endDate: "" }]);
  };
  const handleRemoveMilestone = (e, index) => {
    e.preventDefault();
    if (milestones.length > 1) {
      const updatedMilestones = milestones.filter((_, i) => i !== index);
      setMilestones(updatedMilestones);
    } else {
      toast.error("At least one milestone is required.");
    }
  };

  const handleMilestoneChange = (index, field, value) => {
    const updatedMilestones = milestones.map((milestone, i) =>
      i === index ? { ...milestone, [field]: value } : milestone
    );
    setMilestones(updatedMilestones);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);

    const error = validateLiveField("name", value, { name: "project" });

    setErrors((prev) => ({ ...prev, name: error || undefined }));
  };

  const handleClientNameChange = (event) => {
    setClientName(event.target.value);
  };
  const handleClientCompanyChange = (event) => {
    setClientCompany(event.target.value);
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    const validationPayload = {
      name,
      currency,
    };

    if (hasMilestone) {
      milestones.forEach((m, index) => {
        validationPayload[`milestoneName_${index}`] = m.name;
        validationPayload[`milestoneStartDate_${index}`] = m.startDate;
      });
    } else {
      validationPayload.startDate = milestones[0].startDate;
    }

    const validationErrors = validateFormHandler(validationPayload, {
      name: "project",
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);

      const milestoneIds = [];
      if (hasMilestone) {
        for (const milestone of milestones) {
          try {
            const dataToSend = {
              name: milestone.name,
              startDate: milestone.startDate,
            };
            if (milestone.endDate) {
              dataToSend.endDate = milestone.endDate;
            }
            const milestoneResponse = await api.post(
              getRoute("users", "admin", "projects", false, false, "milestone"),
              dataToSend
            );

            if (milestoneResponse && milestoneResponse.data) {
              milestoneIds.push(milestoneResponse.data);
            } else {
              throw new Error("Milestone ID not found in the response.");
            }
          } catch (error) {
            console.error("Failed to create milestone:", error);
            toast.error(`Failed to create milestone: ${error.message}`);
            return;
          }
        }
      } else {
        const dataToSend = {
          name: name,
          startDate: milestones[0].startDate,
        };
        if (milestones[0].endDate) {
          dataToSend.endDate = milestones[0].endDate;
        }
        // When hasMilestone is false, use project name as milestone name
        const milestoneResponse = await api.post(
          getRoute("users", "admin", "projects", false, false, "milestone"),
          dataToSend
        );

        if (milestoneResponse && milestoneResponse.data) {
          milestoneIds.push(milestoneResponse.data);
        } else {
          throw new Error("Milestone ID not found in the response.");
        }
      }

      const projectResponse = await api.post(
        getRoute("users", "admin", "projects", false, false, ""),
        {
          name,
          currency,
          milestones: milestoneIds,
          hasMilestone,
          description,
          clientName,
          clientCompany,
        }
      );
      if (hasMilestone === true) {
        toast.success("Project and milestones added successfully!");
      } else {
        toast.success("Project added successfully!");
      }
      setName("");
      setCurrency("");
      setClientName("");
      setClientCompany("");
      setDescription("");
      setMilestones([{ name: "", startDate: "", endDate: "" }]);
      setHasMilestone(false);
      toggleModal();

      fetchProjects();
    } catch (error) {
      if (error.response) {
        const errorMessage =
          error.response.data.error || "Failed to add project or milestones.";
        toast.error(`Failed: ${errorMessage}`);
      } else {
        toast.error(
          "An error occurred while adding the project or milestones."
        );
        console.error("Add project/milestone error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const getAllCurrencies = async () => {
    try {
      const { data: currencyData } = await api.get(
        `${API_BASE_URL}/api/v1/getAllCurrencies`
      );
      setCurrencies(currencyData);
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      toast.error("Oops! Error while fetching Expenses");
    }
  };
  useEffect(() => {
    getAllCurrencies();
  }, []);
  useEffect(() => {
    !isOpen && setErrors({});
  }, [isOpen]);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkScrollable = () => {
      setShowFooterShadow(el.scrollHeight > el.clientHeight);
    };

    checkScrollable();

    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [isOpen]);
  return (
    <CustomModal
      modalTitle="Add New Project"
      isOpen={isOpen}
      onClose={toggleModal}
    >
      <form>
        <div
          ref={contentRef}
          className="w-full mx-auto bg-white font-customFont overflow-y-auto scrollbar-hidden max-h-[50vh]"
        >
          <CustomInput
            type="text"
            label="Project Name"
            value={name}
            onChange={handleNameChange}
            placeholder="Enter project name"
            message={errors.name}
            requir="true"
          />
          <div className="mb-4">
            <CustomSelect
              label="Project Currency"
              value={currency}
              onChange={(value) => {
                setCurrency(value);
                if (errors.currency) {
                  setErrors((prev) => ({ ...prev, currency: undefined }));
                }
              }}
              options={currencies.map((currency) => ({
                label: `${currency.symbol} ${currency.name}`,
                value: currency._id,
              }))}
              useAntd
              message={errors.currency}
              requir="true"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <CustomInput
                type="text"
                label="Client Name"
                value={clientName}
                onChange={(e) => {
                  handleClientNameChange(e);
                }}
                placeholder="Enter your client name"
              />
            </div>
            <div className="flex-1">
              <CustomInput
                type="text"
                label="Client Company"
                value={clientCompany}
                onChange={(e) => {
                  handleClientCompanyChange(e);
                }}
                placeholder="Enter client company name"
              />
            </div>
          </div>
          <CustomInput
            label="Description"
            id="description"
            as="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="3"
          />
          <div className="mb-4 flex items-center justify-between mr-1">
            {/* <label className="text-gray-700">Milestone</label> */}
            <CustomInput
              type="checkbox"
              label="Milestone"
              checked={hasMilestone}
              onChange={() => setHasMilestone(!hasMilestone)}
              className="cursor-pointer"
            />
          </div>

          {hasMilestone ? (
            <>
              <div className="flex justify-between items-center">
                <label className="bg-themeGradient text-white rounded-r-full pl-1 pr-2 py-1">
                  Milestones
                </label>
                <button
                  className="bg-[#034ae4] text-white flex justify-center items-center w-[22px] h-[22px] rounded-full"
                  onClick={(e) => handleAddMilestone(e)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
              {milestones.map((milestone, index) => (
                // <div key={index} className="mt-2">
                <div
                  key={index}
                  className="relative mt-4 px-3 py-5 border border-gray-200 rounded-lg shadow-sm bg-[#f9fcfd]"
                >
                  <CustomInput
                    type="text"
                    value={milestone.name}
                    onChange={(e) => {
                      handleMilestoneChange(index, "name", e.target.value);
                      if (errors[`milestoneName_${index}`]) {
                        setErrors((prev) => ({
                          ...prev,
                          [`milestoneName_${index}`]: undefined,
                        }));
                      }
                    }}
                    placeholder="Enter milestone name"
                    message={errors[`milestoneName_${index}`]}
                  />
                  <div className="flex gap-2 w-full mt-1">
                    <span className="w-1/2">
                      <div>
                        <label
                          htmlFor={`milestone-startDate-${index}`}
                          className="block font-medium mb-1 text-gray-700"
                        >
                          Start Date <span className="text-red-500">*</span>
                        </label>

                        <DatePicker
                          id={`milestone-startDate-${index}`}
                          className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                          value={
                            milestone.startDate
                              ? dayjs(milestone.startDate)
                              : null
                          }
                          placeholder="Enter Milestone Start Date"
                          disabledDate={(current) =>
                            milestone.endDate
                              ? current && current > dayjs(milestone.endDate)
                              : false
                          }
                          onChange={(value) => {
                            const selectedDate = value
                              ? value.format("YYYY-MM-DD")
                              : "";
                            handleMilestoneChange(
                              index,
                              "startDate",
                              selectedDate
                            );
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors[`milestoneStartDate_${index}`];
                              return newErrors;
                            });
                          }}
                        />

                        {errors[`milestoneStartDate_${index}`] && (
                          <ErrorText
                            message={errors[`milestoneStartDate_${index}`]}
                          />
                        )}
                      </div>
                    </span>
                    <span className="w-1/2">
                      <div>
                        <label
                          htmlFor={`milestone-endDate-${index}`}
                          className="block font-medium mb-1 text-gray-700"
                        >
                          End Date
                        </label>

                        <DatePicker
                          id={`milestone-endDate-${index}`}
                          className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                          value={
                            milestone.endDate ? dayjs(milestone.endDate) : null
                          }
                          placeholder="Enter Milestone End Date"
                          disabledDate={(current) =>
                            milestone.startDate
                              ? current && current < dayjs(milestone.startDate)
                              : false
                          }
                          onChange={(value) => {
                            const selectedDate = value
                              ? value.format("YYYY-MM-DD")
                              : "";
                            handleMilestoneChange(
                              index,
                              "endDate",
                              selectedDate
                            );
                            if (errors.endDate) {
                              setErrors((prev) => ({
                                ...prev,
                                endDate: undefined,
                              }));
                            }
                          }}
                        />

                        {errors.endDate && (
                          <ErrorText message={errors.endDate} />
                        )}
                      </div>
                    </span>
                  </div>
                  {index > 0 && (
                    <Button
                      type="text"
                      onClick={(e) => handleRemoveMilestone(e, index)}
                      className="!p-0 !m-0 !h-auto !min-h-0 !leading-none absolute top-[-1px] right-[-1px]"
                    >
                      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500/15 border border-red-500">
                        <DeleteOutlined className="text-red-600 text-[10px]" />
                      </span>
                    </Button>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div className="flex gap-2 w-full mt-1">
              {/* Start Date */}
              <span className="w-1/2">
                <div className="mb-4">
                  <label
                    htmlFor={`milestone-0-startDate`}
                    className="block font-medium mb-1 text-gray-700"
                  >
                    Start Date <span className="text-red-500">*</span>
                  </label>

                  <DatePicker
                    id={`milestone-0-startDate`}
                    className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                    value={
                      milestones[0].startDate
                        ? dayjs(milestones[0].startDate)
                        : null
                    }
                    placeholder="Enter Milestone Start Date"
                    disabledDate={(current) =>
                      milestones[0].endDate
                        ? current && current > dayjs(milestones[0].endDate)
                        : false
                    }
                    onChange={(value) => {
                      const selectedDate = value
                        ? value.format("YYYY-MM-DD")
                        : "";
                      handleMilestoneChange(0, "startDate", selectedDate);
                      if (errors.startDate) {
                        setErrors((prev) => ({
                          ...prev,
                          startDate: undefined,
                        }));
                      }
                    }}
                  />

                  {errors.startDate && <ErrorText message={errors.startDate} />}
                </div>
              </span>

              {/* End Date */}
              <span className="w-1/2">
                <div className="mb-4">
                  <label
                    htmlFor={`milestone-0-endDate`}
                    className="block font-medium mb-1 text-gray-700"
                  >
                    End Date
                  </label>

                  <DatePicker
                    id={`milestone-0-endDate`}
                    className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                    value={
                      milestones[0].endDate
                        ? dayjs(milestones[0].endDate)
                        : null
                    }
                    placeholder="Enter Milestone End Date"
                    disabledDate={(current) =>
                      milestones[0].startDate
                        ? current && current < dayjs(milestones[0].startDate)
                        : false
                    }
                    onChange={(value) => {
                      const selectedDate = value
                        ? value.format("YYYY-MM-DD")
                        : "";
                      handleMilestoneChange(0, "endDate", selectedDate);
                      if (errors.endDate) {
                        setErrors((prev) => ({ ...prev, endDate: undefined }));
                      }
                    }}
                  />

                  {errors.endDate && <ErrorText message={errors.endDate} />}
                </div>
              </span>
            </div>
          )}
        </div>
        <ModalFooter
          onCancel={toggleModal}
          isLoading={isLoading}
          onSave={(e) => handleSaveProject(e)}
          showShadow={showFooterShadow}
        />
      </form>
    </CustomModal>
  );
};

export default AddProject;
